import { addMinutes, format, isBefore, parseISO, startOfDay } from 'date-fns'
import { Booking, Extra, Service, Settings, VehicleSize } from '../types/booking'

export const ACTIVE_STATUSES = ['pending', 'confirmed', 'completed']

export function toMinutes(time: string) {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

export function toTime(minutes: number) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function priceFor(
  service: Service | null,
  chosenExtras: Extra[],
  size: VehicleSize | null,
) {
  if (!service) return { base: 0, extras: 0, surcharge: 0, amount: 0, minutes: 0 }
  const extrasTotal = chosenExtras.reduce((sum, e) => sum + e.price, 0)
  const base = service.price
  const surcharge = Math.round(((base + extrasTotal) * (size?.surchargePct ?? 0)) / 100)
  const minutes =
    service.durationMinutes + chosenExtras.reduce((sum, e) => sum + e.durationMinutes, 0)
  return { base, extras: extrasTotal, surcharge, amount: base + extrasTotal + surcharge, minutes }
}

export function depositFor(amount: number, settings: Settings) {
  return Math.round((amount * settings.depositPct) / 100)
}

export type Slot = {
  time: string
  available: boolean
  reason?: 'full' | 'closed' | 'past'
}

function occupancy(bookings: Booking[], dateIso: string) {
  return bookings
    .filter((b) => b.date === dateIso && ACTIVE_STATUSES.includes(b.status))
    .map((b) => ({ start: toMinutes(b.time), end: toMinutes(b.time) + b.durationMinutes }))
}

export function dayIsOpen(dateIso: string, settings: Settings) {
  if (settings.blockedDates.includes(dateIso)) return false
  const weekday = parseISO(dateIso).getDay()
  const hours = settings.hours[weekday]
  return Boolean(hours?.open && hours?.close)
}

export function slotsForDate(
  dateIso: string,
  minutes: number,
  bookings: Booking[],
  settings: Settings,
): Slot[] {
  const weekday = parseISO(dateIso).getDay()
  const hours = settings.hours[weekday]
  if (!hours?.open || !hours?.close || settings.blockedDates.includes(dateIso)) return []

  const open = toMinutes(hours.open)
  const close = toMinutes(hours.close)
  if (minutes > close - open) return []
  const busy = occupancy(bookings, dateIso)
  const earliest = addMinutes(new Date(), settings.leadTimeHours * 60)

  const slots: Slot[] = []
  for (let start = open; start + minutes <= close; start += settings.slotStepMinutes) {
    const end = start + minutes
    const time = toTime(start)
    const slotStart = parseISO(`${dateIso}T${time}:00`)

    if (isBefore(slotStart, earliest)) {
      slots.push({ time, available: false, reason: 'past' })
      continue
    }
    if (end > close) {
      slots.push({ time, available: false, reason: 'closed' })
      continue
    }

    const overlapping = busy.filter((b) => start < b.end && end > b.start).length
    slots.push(
      overlapping >= settings.bays
        ? { time, available: false, reason: 'full' }
        : { time, available: true },
    )
  }

  return slots
}

export type DayState = {
  iso: string
  date: Date
  open: boolean
  free: number
  total: number
  isPast: boolean
}

export function dayState(
  date: Date,
  minutes: number,
  bookings: Booking[],
  settings: Settings,
): DayState {
  const iso = format(date, 'yyyy-MM-dd')
  const slots = slotsForDate(iso, minutes, bookings, settings)
  return {
    iso,
    date,
    open: dayIsOpen(iso, settings),
    free: slots.filter((s) => s.available).length,
    total: slots.length,
    isPast: isBefore(startOfDay(date), startOfDay(new Date())),
  }
}

export function bookingsForDate(bookings: Booking[], dateIso: string) {
  return bookings
    .filter((b) => b.date === dateIso)
    .sort((a, b) => toMinutes(a.time) - toMinutes(b.time))
}

export function utilisationForDate(bookings: Booking[], dateIso: string, settings: Settings) {
  const weekday = parseISO(dateIso).getDay()
  const hours = settings.hours[weekday]
  if (!hours?.open || !hours?.close) return 0
  const capacity = (toMinutes(hours.close) - toMinutes(hours.open)) * settings.bays
  const booked = bookings
    .filter((b) => b.date === dateIso && ACTIVE_STATUSES.includes(b.status))
    .reduce((sum, b) => sum + b.durationMinutes, 0)
  return capacity === 0 ? 0 : Math.min(100, Math.round((booked / capacity) * 100))
}
